import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { X, UserPlus, UserMinus } from "lucide-react";

const MissionEditModal = ({ mission, onClose, onSave }) => {
  const { t } = useTranslation();
  const [type, setType] = useState("");
  const [client, setClient] = useState("");
  const [address, setAddress] = useState("");
  const [departureZip, setDepartureZip] = useState("");
  const [arrivalZip, setArrivalZip] = useState("");
  const [date, setDate] = useState("");
  const [priceHT, setPriceHT] = useState("");
  const [charges, setCharges] = useState("");
  const [employees, setEmployees] = useState([{ employee_name: "", salary: "" }]);

  useEffect(() => {
    if (mission) {
      setType(mission.type || "");
      setClient(mission.client || "");
      setAddress(mission.address || "");
      setDepartureZip(mission.departure_zip || "");
      setArrivalZip(mission.arrival_zip || "");
      setDate(mission.date || "");
      setPriceHT(mission.priceHT || "");
      setCharges(mission.charges || "");
      setEmployees(
        Array.isArray(mission.employees) && mission.employees.length
          ? mission.employees
          : [{ employee_name: "", salary: "" }]
      );
    }
  }, [mission]);

  const handleEmployeeChange = (index, field, value) => {
    const updated = [...employees];
    updated[index][field] = value;
    setEmployees(updated);
  };

  const handleAddEmployee = () => {
    setEmployees([...employees, { employee_name: "", salary: "" }]);
  };

  const handleRemoveEmployee = (index) => {
    if (employees.length === 1) return;
    const updated = [...employees];
    updated.splice(index, 1);
    setEmployees(updated);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const mappedEmployees = employees.map(emp => ({
      employee_name: emp.employee_name || emp.name || "",
      salary: parseFloat(emp.salary || 0)
    }));
    onSave({
      id: mission.id,
      type,
      client,
      address,
      departure_zip: String(departureZip).trim(),
      arrival_zip: String(arrivalZip).trim(),
      date,
      priceHT: parseFloat(priceHT),
      priceTTC: (parseFloat(priceHT) * 1.2).toFixed(2),
      charges: parseFloat(charges),
      employees: mappedEmployees,
    });
  };

  if (!mission) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 bg-base-100 rounded-2xl border border-base-200 shadow-2xl w-full max-w-2xl overflow-y-auto max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-base-200">
          <h2 className="text-lg font-bold text-base-content">
            ✏️ {t("mission.editTitle")}
          </h2>
          <button
            onClick={onClose}
            className="btn btn-ghost btn-sm btn-square text-base-content/40"
            title={t("buttons.cancel")}
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-4">
            {/* Infos générales */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-semibold text-base-content/70">
                  📦 {t("mission.type")}
                </label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  className="select select-bordered w-full bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
                  required
                >
                  <option value="">{t("mission.selectType")}</option>
                  <option value="Moving">🚚 {t("mission.moving")}</option>
                  <option value="Delivery">📦 {t("mission.delivery")}</option>
                </select>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-semibold text-base-content/70">
                  📅 {t("mission.date")}
                </label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="input input-bordered w-full bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
                  required
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-semibold text-base-content/70">
                  👤 {t("mission.client")}
                </label>
                <input
                  type="text"
                  value={client}
                  onChange={(e) => setClient(e.target.value)}
                  className="input input-bordered w-full bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
                  required
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-semibold text-base-content/70">
                  📍 {t("mission.address")}
                </label>
                <input
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  className="input input-bordered w-full bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-semibold text-base-content/70">
                  📍 {t("mission.form.departureZip", "CP départ")}
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={departureZip}
                  onChange={(e) => setDepartureZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
                  placeholder="75015"
                  className="input input-bordered w-full bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-semibold text-base-content/70">
                  📍 {t("mission.form.arrivalZip", "CP arrivée")}
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={arrivalZip}
                  onChange={(e) => setArrivalZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
                  placeholder="37000"
                  className="input input-bordered w-full bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-semibold text-base-content/70">
                  💵 {t("mission.priceHT")}
                </label>
                <input
                  type="number"
                  value={priceHT}
                  onChange={(e) => setPriceHT(e.target.value)}
                  className="input input-bordered w-full bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
                  required
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-semibold text-base-content/70">
                  📈 {t("mission.charges")}
                </label>
                <input
                  type="number"
                  value={charges}
                  onChange={(e) => setCharges(e.target.value)}
                  className="input input-bordered w-full bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
                />
              </div>
            </div>

            {/* Prix TTC */}
            <div className="flex justify-end">
              <span className="text-sm font-semibold text-base-content/70">
                🧾 {t("mission.priceTTC")}:{" "}
                <span className="text-primary font-bold">
                  {(parseFloat(priceHT || 0) * 1.2).toFixed(2)} €
                </span>
              </span>
            </div>

            {/* Employés */}
            <div>
              <label className="block text-sm font-semibold text-base-content/70 mb-2">
                👷 {t("mission.employees")}
              </label>
              <div className="space-y-2">
                {employees.map((emp, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <input
                      type="text"
                      placeholder={t("mission.employeeName")}
                      value={emp.employee_name || emp.name || ""}
                      onChange={(e) => handleEmployeeChange(idx, "employee_name", e.target.value)}
                      className="flex-1 input input-bordered input-sm bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
                      required
                    />
                    <input
                      type="number"
                      placeholder={t("mission.salary")}
                      value={emp.salary}
                      onChange={(e) => handleEmployeeChange(idx, "salary", e.target.value)}
                      className="w-32 input input-bordered input-sm bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveEmployee(idx)}
                      className="btn btn-ghost btn-sm btn-square text-error hover:bg-error/10"
                      disabled={employees.length === 1}
                      title={t("buttons.remove")}
                    >
                      <UserMinus size={16} />
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={handleAddEmployee}
                className="mt-3 btn btn-ghost btn-sm text-base-content/70 hover:bg-base-200 flex items-center gap-2"
              >
                <UserPlus size={16} /> {t("buttons.addEmployee")}
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-2 px-6 py-4 border-t border-base-200">
            <button type="button" onClick={onClose} className="btn btn-ghost">
              {t("buttons.cancel")}
            </button>
            <button type="submit" className="btn btn-primary">
              {t("buttons.save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MissionEditModal;
